// Coque de l'application : navigation (5 entrées), barre supérieure, recherche, bandeaux, pied de page.
import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, Link } from 'react-router';
import {
  Boxes, Building2, BookOpen, ClipboardList, FileSearch, FileSignature, Home, LogOut, Moon, Search, Settings, Sun, SunMoon, TrendingUp, UserRound,
} from 'lucide-react';
import { useSession } from '../lib/session.tsx';
import { useModules } from '../lib/modules.ts';
import { useTheme, useVue } from '../lib/preferences.ts';
import { BandeauConnexion, useDeconnexion, useEnvoiAutomatique } from './horsConnexion.tsx';
import { RechercheGlobale } from './RechercheGlobale.tsx';
import { PiedDePage } from './PiedDePage.tsx';

export const ENTREES = [
  { to: '/', libelle: 'Accueil', court: 'Accueil', icone: <Home aria-hidden />, fin: true },
  { to: '/interventions', libelle: 'Interventions', court: 'Interventions', icone: <ClipboardList aria-hidden /> },
  { to: '/etudes', libelle: 'Études et devis', court: 'Études', icone: <FileSearch aria-hidden /> },
  { to: '/documentation', libelle: 'Documentation', court: 'Docs', icone: <BookOpen aria-hidden /> },
  { to: '/sites', libelle: 'Sites et équipements', court: 'Sites', icone: <Building2 aria-hidden /> },
];

function Marque() {
  const { moi } = useSession();
  return (
    <Link to="/" className="marque" aria-label="TRAME — accueil">
      <svg width="28" height="28" viewBox="0 0 28 28" aria-hidden>
        <rect x="1" y="1" width="26" height="26" rx="6" fill="none" stroke="var(--accent)" strokeWidth="2" />
        <path d="M7 9h14M7 14h14M7 19h14M10 6v16M18 6v16" stroke="var(--encre)" strokeWidth="1.6" strokeLinecap="round" />
        <circle cx="18" cy="14" r="2.6" fill="var(--accent)" />
      </svg>
      <span className="pile" style={{ gap: 0 }}>
        <span className="marque-nom">TRAME</span>
        <span className="marque-org">{moi.organisation.nom}</span>
      </span>
    </Link>
  );
}

export function Coque() {
  const { moi, peut } = useSession();
  const modules = useModules();
  const gamme = !!modules.ma_gamme && peut('gamme.lire');
  // Option « Contrats de maintenance » : l'entrée sert à l'encadrement ; le technicien passe par ses visites (Interventions).
  const contrats = !!modules.contrats_maintenance && peut('contrats.lire') && (peut('contrats.ecrire') || peut('visites.planifier'));
  const [theme, setTheme] = useTheme();
  const [vue, setVue] = useVue();
  const [recherche, setRecherche] = useState(false);
  const loc = useLocation();
  // Hors connexion (incrément 6) : la file d'envoi part seule au retour du réseau ; la déconnexion est bloquée tant qu'elle n'est pas vide.
  useEnvoiAutomatique();
  const { deconnecter, element: elementDeconnexion } = useDeconnexion();
  // Vue Terrain : « Interventions » ouvre « Ma journée » (la page d'entrée naturelle du technicien) ; la section reste
  // signalée active sur toutes ses pages (journée, chantiers, planning, dépannages).
  const cibleInterventions = vue === 'terrain' && peut('chantier.lire') ? '/interventions/journee' : '/interventions';
  const dansInterventions = loc.pathname === '/interventions' || loc.pathname.startsWith('/interventions/');
  const lien = (e: (typeof ENTREES)[number], cls: string | undefined, contenu: ReactNode) => (e.to === '/interventions'
    ? <Link key={e.to} to={cibleInterventions} className={cls} aria-current={dansInterventions ? 'page' : undefined}>{contenu}</Link>
    : <NavLink key={e.to} to={e.to} end={e.fin} className={cls}>{contenu}</NavLink>);

  useEffect(() => {
    const touche = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setRecherche(true); }
      if (e.key === '/' && !['INPUT', 'TEXTAREA', 'SELECT'].includes((e.target as HTMLElement)?.tagName)) { e.preventDefault(); setRecherche(true); }
    };
    window.addEventListener('keydown', touche);
    return () => window.removeEventListener('keydown', touche);
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, [loc.pathname]);

  const iconeTheme = theme === 'dark' ? <Moon aria-hidden /> : theme === 'light' ? <Sun aria-hidden /> : <SunMoon aria-hidden />;
  const libelleTheme = theme === 'dark' ? 'Thème sombre' : theme === 'light' ? 'Thème clair' : 'Thème du système';
  const suivant = theme === 'systeme' ? 'light' : theme === 'light' ? 'dark' : 'systeme';

  return (
    <div className="coque">
      <a href="#contenu" className="evitement">Aller au contenu</a>
      {elementDeconnexion}
      <aside className="coque-rail" aria-label="Navigation principale">
        <Marque />
        <nav className="nav">
          {ENTREES.map((e) => lien(e, 'nav-lien', <>{e.icone}{e.libelle}</>))}
        </nav>
        <div className="nav-pied">
          {contrats && <NavLink to="/contrats" className="nav-lien"><FileSignature aria-hidden />Contrats</NavLink>}
          {gamme && <NavLink to="/gamme" className="nav-lien"><Boxes aria-hidden />Ma gamme</NavLink>}
          {peut('rentabilite.lire') && <NavLink to="/rentabilite" className="nav-lien"><TrendingUp aria-hidden />Rentabilité</NavLink>}
          {peut('admin.utilisateurs') && <NavLink to="/administration" className="nav-lien"><Settings aria-hidden />Administration</NavLink>}
          <NavLink to="/compte" className="nav-lien"><UserRound aria-hidden /><span className="pile" style={{ gap: 0, minWidth: 0 }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{moi.utilisateur.nom}</span>
            <span className="tres-petit discret">{moi.roles.map((r) => r.libelle).join(', ')}</span></span></NavLink>
          <button type="button" className="nav-lien" style={{ border: 0, background: 'none', cursor: 'pointer', width: '100%', textAlign: 'left' }} onClick={deconnecter}>
            <LogOut aria-hidden />Se déconnecter
          </button>
        </div>
      </aside>

      <header className="coque-barre">
        <button type="button" className="recherche-declencheur" onClick={() => setRecherche(true)} aria-label="Rechercher (référence, code défaut, équipement, site, document)">
          <Search size={18} aria-hidden /><span className="masque-etroit">Référence, code défaut, équipement, site…</span><span className="visible-etroit">Rechercher</span><kbd>Ctrl K</kbd>
        </button>
        <div className="segment masque-etroit" role="group" aria-label="Densité d'affichage">
          <button type="button" aria-pressed={vue === 'terrain'} onClick={() => setVue('terrain')} title="Vue Terrain : grands contrôles, l'essentiel">Terrain</button>
          <button type="button" aria-pressed={vue === 'expert'} onClick={() => setVue('expert')} title="Vue Expert : toutes les colonnes et détails">Expert</button>
        </div>
        <button type="button" className="btn btn-fantome btn-icone" onClick={() => setTheme(suivant)} aria-label={`${libelleTheme} — changer`} title={libelleTheme}>{iconeTheme}</button>
        <Link to="/compte" className="btn btn-fantome btn-icone barre-compte" aria-label="Mon compte"><UserRound aria-hidden /></Link>
      </header>

      <div className="coque-contenu">
        <BandeauConnexion />
        <main id="contenu" className="coque-page" tabIndex={-1}>
          <Outlet context={{ setVue }} />
        </main>
        <PiedDePage />
      </div>

      <nav className="onglets-bas" aria-label="Navigation principale">
        {ENTREES.map((e) => lien(e, undefined, <>{e.icone}<span>{e.court}</span></>))}
      </nav>

      <RechercheGlobale ouvert={recherche} fermer={() => setRecherche(false)} />
    </div>
  );
}

export function PageSimple({ children }: { children: ReactNode }) {
  return <div className="pile-l">{children}</div>;
}
